import _merge from 'lodash/merge';

import { placeholders } from 'components/Phone/placeholders';
import { WedgeColors } from 'containers/Campaign/Games/SpinTheWheel/Assets';
import { withWinProbability } from 'containers/Campaign/Games/winOdds';
import { OUTCOME_TYPE } from 'pages/campaign/constant';

import { TOrbit } from '../../../i18n/TModules';
import { MAX_WEDGES, MIN_WEDGES, WizardGame, WizardState, WizardWedge } from '../wizardModel';
import { CampaignFormValues, WheelWedgeValue } from '../wizardSubmission';
import {
  AdapterContext,
  baseValues,
  intervalValues,
  limitValue,
  positive,
  prizeComplete,
  toOutcome
} from './common';
import { Translate } from './ruleAdapter';

const TG = `${TOrbit}.wizard.game`;

/** The module's "No outcome" row (`PrizeSet`). */
const noOutcomeRow = (probability: number) => ({
  id: 'no_outcome',
  name: 'No outcome',
  type: OUTCOME_TYPE.CUSTOM,
  identifier: 'no_outcome',
  probability
});

/** Choice mode always carries the no-prize row: it is where the win odds live. */
export const hasNoPrizeRow = (game: WizardGame) => game.mode === 'choice' || game.noPrize;

export const outcomeErrors = (game: WizardGame, t: Translate): string[] => {
  const errors: string[] = [];
  if (game.outcomes.length === 0 || !game.outcomes.every(prizeComplete)) {
    errors.push(t(`${TOrbit}.wizard.prize.incomplete`));
  }
  if (game.mode === 'choice' && game.outcomes.length < 2) {
    errors.push(t(`${TG}.choiceNeedsTwo`));
  }
  return errors;
};

/** Random mode: the drawn percentages, the no-prize row included. */
export const probabilityTotal = (game: WizardGame): number =>
  [
    ...game.outcomes.map(outcome => outcome.probability),
    ...(game.noPrize ? [game.noPrizeProbability] : [])
  ]
    .map(value => Number(value) || 0)
    .reduce((sum, value) => Math.round((sum + value) * 100) / 100, 0);

/** Every outcome at 1 % or more (the module rejects 0), and 100 % in all. */
export const probabilityErrors = (game: WizardGame, t: Translate): string[] => {
  if (game.mode === 'choice') {
    return [];
  }
  const values = [
    ...game.outcomes.map(outcome => outcome.probability),
    ...(game.noPrize ? [game.noPrizeProbability] : [])
  ];
  if (values.some(value => !positive(value))) {
    return [t(`${TOrbit}.wizard.limits.positive`)];
  }
  const total = probabilityTotal(game);
  if (total === 100) {
    return [];
  }
  return [
    total < 100 ? t(`${TG}.remaining`, { n: 100 - total }) : t(`${TG}.over`, { n: total - 100 })
  ];
};

const gameOutcomes = (game: WizardGame) => {
  const winners = game.outcomes.map(outcome => toOutcome(outcome));
  if (game.mode === 'choice') {
    // The win odds live on outcome_choices_config; the module drops per-outcome probability here.
    return [...winners, noOutcomeRow(0)];
  }
  const weighted = winners.map((outcome, index) => ({
    ...outcome,
    probability: Number(game.outcomes[index].probability)
  }));
  return game.noPrize ? [...weighted, noOutcomeRow(Number(game.noPrizeProbability))] : weighted;
};

/** The most wedges the module's wheel draws today (`SpinTheWheel` offers 4 to 8). */
export const MODULE_MAX_WEDGES = 8;

const wedgeComplete = (wedge: WizardWedge) =>
  wedge.type === 'none' ||
  (wedge.type === 'points'
    ? positive(wedge.points) && wedge.loyalty !== null
    : wedge.reward !== null);

/** Every wedge set, and at least one that wins (the module wants a reward on one wedge). */
export const wedgeErrors = (wedges: WizardWedge[], t: Translate): string[] => {
  const errors: string[] = [];
  if (!wedges.every(wedgeComplete)) {
    errors.push(t(`${TOrbit}.wizard.prize.incomplete`));
  }
  if (wedges.every(wedge => wedge.type === 'none')) {
    errors.push(t(`${TG}.wheel.oneWinner`));
  }
  return errors;
};

export const wedgeCountErrors = (count: number, t: Translate): string[] =>
  count >= MIN_WEDGES && count <= MAX_WEDGES ? [] : [t(`${TG}.wheel.range`)];

const wedgeOutcome = (wedge: WizardWedge) =>
  wedge.type === 'none' ? null : toOutcome({ ...wedge, type: wedge.type });

/**
 * The wheel in the module's shape as well: each distinct prize once, weighted by the wedges that
 * carry it (each wedge equally likely), a no-prize row for the empty wedges, and `has_reward` per
 * wedge. Which prize sits on which wedge survives only in `wheel_wedges`.
 */
const wheelValues = (game: WizardGame, seed: Record<string, unknown>) => {
  const count = game.wedges.length;
  const share = (n: number) => Math.round((n / count) * 10000) / 100;
  const prizes = new Map<string, { outcome: Record<string, unknown>; wedges: number }>();
  game.wedges.forEach(wedge => {
    const outcome = wedgeOutcome(wedge);
    if (outcome !== null) {
      const key = JSON.stringify(outcome);
      const known = prizes.get(key);
      prizes.set(key, { outcome, wedges: (known?.wedges ?? 0) + 1 });
    }
  });
  const empty = game.wedges.filter(wedge => wedge.type === 'none').length;
  const wheelWedges: WheelWedgeValue[] = game.wedges.map((wedge, position) => ({
    position,
    outcome: wedgeOutcome(wedge)
  }));
  return {
    display_properties: {
      ...seed,
      number_of_wedges: count,
      wedges: game.wedges.map((wedge, position) => ({
        position,
        color: WedgeColors[position % WedgeColors.length],
        image: '',
        has_reward: wedge.type !== 'none'
      }))
    },
    outcomes: [
      ...Array.from(prizes.values()).map(({ outcome, wedges }) => ({
        ...outcome,
        probability: share(wedges)
      })),
      ...(empty > 0 ? [noOutcomeRow(share(empty))] : [])
    ],
    outcome_choices_config: { enabled: false },
    wheel_wedges: wheelWedges
  };
};

export const gameValues = (state: WizardState, context: AdapterContext): CampaignFormValues => {
  const { game, limits } = state;
  const gameType = game.gameType ?? 'shake_the_tree';
  const seed = placeholders[gameType];
  return {
    ...baseValues(state, 'game', context, true),
    game_type: gameType,
    display_properties_en: _merge({}, seed.translatable),
    ...(gameType === 'spin_the_wheel'
      ? wheelValues(game, seed.static)
      : {
          display_properties: { ...seed.static },
          outcomes: gameOutcomes(game),
          outcome_choices_config:
            game.mode === 'choice'
              ? withWinProbability({ enabled: true }, game.winProbability)
              : { enabled: false }
        }),
    auto_issue_moves: limits.autoIssue,
    campaign_lifetime_max_moves_available: limitValue(limits.totalOn, limits.total),
    account_lifetime_max_moves_available: limitValue(limits.perUserOn, limits.perUser),
    ...intervalValues(
      'account',
      'account_interval_max_moves_available',
      limits.userRateOn,
      limits.userRate,
      limits.userRatePeriod
    )
  };
};
